import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, Trophy, MapPin, Users, X, Sun, Settings, CheckCircle2, TicketCheck, ArrowLeft } from 'lucide-react';
import { createPageUrl } from '@/utils';


export default function Admin({ onClose }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingBonus, setTogglingBonus] = useState(false);
  const [activatingPhase, setActivatingPhase] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

 // Auto-scroll to bottom on mobile
 useEffect(() => {
   if (window.innerWidth < 768) {
     setTimeout(() => {
       window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
     }, 100);
   }
 }, []);

 useEffect(() => {
   supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
       if (!authUser) { navigate('/map'); return; }
       const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
       if (!profile || profile.role !== 'admin') {
         navigate('/map');
       } else {
         setUser({ ...authUser, ...profile });
       }
     }).catch(() => {
       navigate('/map');
     })
     .finally(() => setIsLoading(false));
 }, []);


 const { data: stats = {} } = useQuery({
   queryKey: ['adminStats'],
   queryFn: async () => {
     const challenges = await base44.entities.Challenge.list('-created_date', 100);
     const courts = await base44.entities.Playground.list('-created_date', 100);
     const raffleEntries = await base44.entities.RaffleEntry.list('-created_date', 1000);
     const draws = await base44.entities.ChallengeDraw.list('-created_date', 100);
    
     const activeChallenges = challenges.filter(c => c.is_active && c.status === 'active').length;
     const closedChallenges = challenges.filter(c => c.status === 'closed').length;
     const drawnChallenges = challenges.filter(c => c.status === 'drawn').length;
     const pendingCourts = courts.filter(c => c.validation_status === 'unvalidated').length;
     const activeParticipants = new Set(raffleEntries.map(e => e.user_id)).size;
     const totalTicketsInPlay = raffleEntries.length;
    
     return {
       activeChallenges,
       closedChallenges,
       drawnChallenges,
       pendingCourts,
       activeParticipants,
       totalTicketsInPlay,
       totalChallenges: challenges.length,
       totalCourts: courts.length,
       totalDrawsExecuted: draws.length
     };
   },
   enabled: !!user?.id,
   refetchOnWindowFocus: true,
   staleTime: 0
 });


 // Weekend bonus state
 const { data: weekendBonusEnabled = false } = useQuery({
   queryKey: ['weekendBonusStatus'],
   queryFn: async () => {
     const logs = await base44.entities.AdminLog.list('-timestamp', 100);
     const toggleLog = logs.find(l => l.action_type === 'weekend_bonus_toggle' && l.target_id === 'weekend_bonus');
     return toggleLog?.details?.enabled === true;
   },
   enabled: !!user?.id,
   staleTime: 0
 });

 // Phase management
 const { data: phases = [] } = useQuery({
   queryKey: ['adminPhases'],
   queryFn: async () => {
     return await base44.entities.Phase.list('-created_date', 50);
   },
   enabled: !!user?.id,
   staleTime: 0
 });

 const activePhase = phases.find(p => p.active === true);

 const handleActivatePhase = async (phaseId) => {
   setActivatingPhase(phaseId);
   try {
     await supabase.from('phases').update({ active: false }).neq('phase_id', phaseId);
     await supabase.from('phases').update({ active: true }).eq('phase_id', phaseId);
     queryClient.invalidateQueries({ queryKey: ['adminPhases'] });
   } catch (err) {
     console.error('Failed to activate phase:', err);
   } finally {
     setActivatingPhase(null);
   }
 };

 const handleToggleWeekendBonus = async () => {
   setTogglingBonus(true);
   try {
     console.warn('[Migration] toggleWeekendBonus not fully migrated yet');
     queryClient.invalidateQueries({ queryKey: ['weekendBonusStatus'] });
   } catch (err) {
     console.error('Failed to toggle weekend bonus:', err);
   } finally {
     setTogglingBonus(false);
   }
 };

 if (isLoading) {
   return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
 }


 if (!user) {
   return null;
 }


 const content = (
     <div className="p-4 md:p-6">
       <div className="flex justify-between items-start mb-6">
         <div className="flex items-center gap-3">
           {!onClose && (
             <Button
               variant="ghost"
               size="sm"
               onClick={() => navigate(-1)}
               className="text-white hover:text-[#6E2BFF] p-2 -ml-2"
             >
               <ArrowLeft className="w-5 h-5" />
             </Button>
           )}
           <Badge className="bg-[#6E2BFF] text-white text-xs font-bold">ADMIN</Badge>
           <div>
             <h2 className="text-lg font-black uppercase tracking-wide text-white">Admin Dashboard</h2>
             <p className="text-[#B3B6C2] text-xs md:text-sm mt-1">Manage challenges, courts, and community</p>
           </div>
         </div>
         {onClose && (
           <Button
             variant="ghost"
             size="sm"
             onClick={onClose}
             className="text-white hover:text-[#6E2BFF]"
           >
             <X className="w-5 h-5" />
           </Button>
         )}
       </div>

     <div className="max-w-5xl mx-auto">


       {/* Weekend Bonus Toggle */}
       <Card className={`mb-6 p-4 border-2 transition-all ${weekendBonusEnabled ? 'bg-[#1a1a0f] border-yellow-500' : 'bg-[#13151D] border-[#2A2D3A]'}`}>
         <div className="flex items-center justify-between">
           <div className="flex items-center gap-3">
             <Sun className={`w-6 h-6 ${weekendBonusEnabled ? 'text-yellow-400' : 'text-[#B3B6C2]'}`} />
             <div>
               <p className="text-sm font-bold text-white">Bonus Week-end (+20% XP)</p>
               <p className="text-xs text-[#B3B6C2]">
                 {weekendBonusEnabled ? 'Actif — annonce envoyée aux joueurs' : 'Désactivé'}
               </p>
             </div>
           </div>
           <Button
             size="sm"
             disabled={togglingBonus}
             onClick={handleToggleWeekendBonus}
             className={weekendBonusEnabled
               ? 'bg-yellow-500 hover:bg-yellow-600 text-black font-bold'
               : 'bg-[#6E2BFF] hover:bg-[#5a23d6] text-white font-bold'}
           >
             {togglingBonus ? '...' : weekendBonusEnabled ? 'Désactiver' : 'Activer'}
           </Button>
         </div>
       </Card>

       {/* Phase Management */}
       <Card className="mb-6 p-4 border-2 bg-[#13151D] border-[#2A2D3A]">
         <div className="flex items-center gap-3 mb-3">
           <Settings className="w-6 h-6 text-[#6E2BFF]" />
           <div>
             <p className="text-sm font-bold text-white">Phase active</p>
             <p className="text-xs text-[#B3B6C2]">
               {activePhase ? `${activePhase.phase_id}` : 'Aucune phase active (règles par défaut)'}
             </p>
           </div>
         </div>
         {phases.length > 0 ? (
           <div className="flex flex-wrap gap-2 mt-2">
             {phases.map(p => {
               const isActive = p.active === true;
               const isActivating = activatingPhase === p.phase_id;
               let rules;
               try {
                 rules = typeof p.rules_json === 'string' ? JSON.parse(p.rules_json) : p.rules_json;
               } catch { rules = {}; }
               return (
                 <button
                   key={p.id}
                   disabled={isActive || isActivating}
                   onClick={() => handleActivatePhase(p.phase_id)}
                   className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                     isActive
                       ? 'bg-[#6E2BFF] text-white cursor-default'
                       : 'bg-[#1B1E28] text-[#B3B6C2] hover:bg-[#252836] hover:text-white border border-[#2A2D3A]'
                   }`}
                   title={rules ? JSON.stringify(rules, null, 2) : ''}
                 >
                   {isActive && <CheckCircle2 className="w-3 h-3" />}
                   {isActivating ? '...' : p.phase_id}
                 </button>
               );
             })}
           </div>
         ) : (
           <p className="text-xs text-[#B3B6C2] mt-1">
             Aucune phase créée. Créer des entrées Phase dans le dashboard Base44.
           </p>
         )}
       </Card>

       {/* Quick Stats Grid */}
       <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
         <Card className="bg-[#13151D] border-l-4 border-orange-600 p-4">
           <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Terrains en attente</p>
           <p className="text-3xl font-black text-orange-500">{stats.pendingCourts || 0}</p>
         </Card>
         <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-4">
           <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Total terrains</p>
           <p className="text-3xl font-black text-[#6E2BFF]">{stats.totalCourts || 0}</p>
         </Card>
       </div>


       {/* Navigation Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         {/* Challenges */}
         <div onClick={() => navigate('/admin/challenges')}>
          <Card className="bg-[#13151D] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:bg-[#1B1E28] transition-all p-6 cursor-pointer h-full text-white">
            <div className="flex items-start justify-between mb-4">
              <Trophy className="w-8 h-8 text-[#6E2BFF]" />
              <ArrowRight className="w-5 h-5 text-[#6E2BFF]" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight mb-2">Challenges</h2>
             <p className="text-sm text-[#B3B6C2] mb-4">Créer, activer, fermer et exécuter des tirages</p>
             <div className="flex items-center gap-2 text-xs text-[#6E2BFF] font-bold">
               <span>{stats.totalChallenges || 0} total</span>
               <ArrowRight className="w-3 h-3" />
             </div>
            </Card>
            </div>

            {/* Courts */}
            <div onClick={() => navigate('/admin/courts')}>
             <Card className="bg-[#13151D] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:bg-[#1B1E28] transition-all p-6 cursor-pointer h-full text-white">
            <div className="flex items-start justify-between mb-4">
              <MapPin className="w-8 h-8 text-[#6E2BFF]" />
              <ArrowRight className="w-5 h-5 text-[#6E2BFF]" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight mb-2">Terrains</h2>
             <p className="text-sm text-[#B3B6C2] mb-4">Surveiller la validation, l'activité et le statut</p>
             <div className="flex items-center gap-2 text-xs text-[#6E2BFF] font-bold">
               <span>{stats.totalCourts} total</span>
               <ArrowRight className="w-3 h-3" />
             </div>
             </Card>
             </div>

             {/* Users */}
             <div onClick={() => navigate('/admin/users')}>
             <Card className="bg-[#13151D] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:bg-[#1B1E28] transition-all p-6 cursor-pointer h-full text-white">
            <div className="flex items-start justify-between mb-4">
              <Users className="w-8 h-8 text-[#6E2BFF]" />
              <ArrowRight className="w-5 h-5 text-[#6E2BFF]" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight mb-2">Utilisateurs</h2>
             <p className="text-sm text-[#B3B6C2] mb-4">Voir les stats, XP et activité des utilisateurs</p>
             <div className="flex items-center gap-2 text-xs text-[#6E2BFF] font-bold">
               <span>Vue d'ensemble communauté</span>
               <ArrowRight className="w-3 h-3" />
             </div>
             </Card>
             </div>

             {/* Audit Log */}
             <div onClick={() => navigate('/admin/logs')}>
             <Card className="bg-[#13151D] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:bg-[#1B1E28] transition-all p-6 cursor-pointer h-full text-white">
            <div className="flex items-start justify-between mb-4">
              <Zap className="w-8 h-8 text-[#6E2BFF]" />
              <ArrowRight className="w-5 h-5 text-[#6E2BFF]" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight mb-2">Journal d'audit</h2>
             <p className="text-sm text-[#B3B6C2] mb-4">Consulter toutes les actions et modifications admin</p>
             <div className="flex items-center gap-2 text-xs text-[#6E2BFF] font-bold">
               <span>Traçabilité</span>
               <ArrowRight className="w-3 h-3" />
             </div>
             </Card>
             </div>

             {/* Affiliation Codes */}
             <div onClick={() => navigate('/admin/codes')}>
             <Card className="bg-[#13151D] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:bg-[#1B1E28] transition-all p-6 cursor-pointer h-full text-white">
            <div className="flex items-start justify-between mb-4">
              <TicketCheck className="w-8 h-8 text-[#6E2BFF]" />
              <ArrowRight className="w-5 h-5 text-[#6E2BFF]" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight mb-2">Codes d'invitation</h2>
             <p className="text-sm text-[#B3B6C2] mb-4">Créer des codes d'affiliation pour ouvrir de nouvelles villes</p>
             <div className="flex items-center gap-2 text-xs text-[#6E2BFF] font-bold">
               <span>Gérer les codes</span>
               <ArrowRight className="w-3 h-3" />
             </div>
             </Card>
             </div>
             </div>
         </div>
         </div>
         );

         return content;
         }
        